/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useState, memo } from "react";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import {
  FunctionDeclaration,
  LiveServerToolCall,
  Modality,
  Type,
} from "@google/genai";
import { 
  fetchClientDetails, 
  fetchLatestOffers, 
  saveReview, 
  bookAppointment,
  getAvailableTimeSlots,
  formatLastVisit, 
  formatOffers,
  validateOffer,
  scheduleCallback,
  Client,
  Offer,
  Review,
  Appointment,
  CallbackSchedule
} from "../../lib/scheduling-data";
import "./scheduling-agent.scss";

const fetchClientDetailsDeclaration: FunctionDeclaration = {
  name: "fetch_client_details",
  description: "Fetches client details including last visit information using their name and date of birth.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      name: {
        type: Type.STRING,
        description: "The client's full name",
      },
      dateOfBirth: {
        type: Type.STRING,
        description: "The client's date of birth in YYYY-MM-DD format",
      },
    },
    required: ["name", "dateOfBirth"],
  },
};

const saveReviewDeclaration: FunctionDeclaration = {
  name: "save_review",
  description: "Saves a client's review and rating for their visit.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      clientId: {
        type: Type.STRING,
        description: "The client's ID",
      },
      clientName: {
        type: Type.STRING,
        description: "The client's name",
      },
      visitDate: {
        type: Type.STRING,
        description: "The date of the visit being reviewed",
      },
      rating: {
        type: Type.NUMBER,
        description: "Rating from 1-5 stars",
      },
      review: {
        type: Type.STRING,
        description: "The client's review text",
      },
      stylist: {
        type: Type.STRING,
        description: "The stylist who provided the service",
      },
      services: {
        type: Type.ARRAY,
        description: "Array of services provided",
        items: {
          type: Type.STRING,
        },
      },
      recommended: {
        type: Type.BOOLEAN,
        description: "Whether the client would recommend the salon",
      },
    },
    required: ["clientId", "clientName", "visitDate", "rating", "review", "stylist", "services", "recommended"],
  },
};

const fetchLatestOffersDeclaration: FunctionDeclaration = {
  name: "fetch_latest_offers",
  description: "Fetches the latest offers and promotions available at the salon.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
    required: [],
  },
};

const validateOfferDeclaration: FunctionDeclaration = {
  name: "validate_offer",
  description: "Validates if a specific offer is currently valid and applicable to the client's services.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      offerId: {
        type: Type.STRING,
        description: "The offer ID to validate",
      },
      services: {
        type: Type.ARRAY,
        description: "Array of services the client wants to book (optional)",
        items: {
          type: Type.STRING,
        },
      },
    },
    required: ["offerId"],
  },
};

const bookAppointmentDeclaration: FunctionDeclaration = {
  name: "book_appointment",
  description: "Books a new appointment for the client. Client can optionally select ONE discount/offer to apply to the booking. IMPORTANT: Always validate offers first using validate_offer before booking.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      clientId: {
        type: Type.STRING,
        description: "The client's ID",
      },
      clientName: {
        type: Type.STRING,
        description: "The client's name",
      },
      date: {
        type: Type.STRING,
        description: "The appointment date in YYYY-MM-DD format",
      },
      time: {
        type: Type.STRING,
        description: "The appointment time in HH:MM format",
      },
      services: {
        type: Type.ARRAY,
        description: "Array of services to be provided",
        items: {
          type: Type.STRING,
        },
      },
      stylist: {
        type: Type.STRING,
        description: "The preferred stylist name",
      },
      estimatedDuration: {
        type: Type.NUMBER,
        description: "Estimated duration of the appointment in minutes",
      },
      estimatedCost: {
        type: Type.NUMBER,
        description: "Estimated cost of the services BEFORE any discounts",
      },
      notes: {
        type: Type.STRING,
        description: "Important notes for the appointment including special requests, allergies, styling preferences, or any other relevant information the client mentions",
      },
      selectedOfferId: {
        type: Type.STRING,
        description: "Optional offer ID to apply discount to the booking. Client can select only ONE offer. Must be validated first.",
      },
    },
    required: ["clientId", "clientName", "date", "time", "services", "stylist", "estimatedDuration", "estimatedCost"],
  },
};

const scheduleCallbackDeclaration: FunctionDeclaration = {
  name: "schedule_callback",
  description: "Schedules a callback for when the client is not interested in booking immediately, is busy, or wants to reschedule the conversation.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      clientId: {
        type: Type.STRING,
        description: "The client's ID",
      },
      clientName: {
        type: Type.STRING,
        description: "The client's name",
      },
      callbackDate: {
        type: Type.STRING,
        description: "The preferred callback date in YYYY-MM-DD format",
      },
      callbackTime: {
        type: Type.STRING,
        description: "The preferred callback time in HH:MM format",
      },
      reason: {
        type: Type.STRING,
        description: "Reason for callback: 'not_interested', 'busy', 'reschedule', or 'other'",
        enum: ["not_interested", "busy", "reschedule", "other"],
      },
      notes: {
        type: Type.STRING,
        description: "Additional notes about the callback request, client preferences, or reason for postponing",
      },
      preferredMethod: {
        type: Type.STRING,
        description: "Client's preferred contact method: 'phone', 'email', or 'either'",
        enum: ["phone", "email", "either"],
      },
    },
    required: ["clientId", "clientName", "callbackDate", "callbackTime", "reason", "preferredMethod"],
  },
};

const getTodayDateDeclaration: FunctionDeclaration = {
  name: "get_today_date",
  description: "Returns today's date in YYYY-MM-DD format.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
    required: [],
  },
};

interface SchedulingComponentProps {
  selectedClient?: {
    id: string;
    name: string;
    dateOfBirth: string;
    phone: string;
    email: string;
    lastVisit: {
      date: string;
      services: string[];
      stylist: string;
      totalCost: number;
      notes?: string;
    };
  } | null;
}

function SchedulingComponent({ selectedClient }: SchedulingComponentProps) {
  const [clientData, setClientData] = useState<Client | null>(null);
  const [currentOffers, setCurrentOffers] = useState<Offer[]>([]);
  const [bookedAppointment, setBookedAppointment] = useState<Appointment | null>(null);
  const [scheduledCallback, setScheduledCallback] = useState<CallbackSchedule | null>(null);
  const [lastAction, setLastAction] = useState<string>("");
  const { client, setConfig, setModel } = useLiveAPIContext();

  // Set client data when selectedClient is provided
  useEffect(() => {
    if (selectedClient) {
      // Convert selectedClient to Client format and set it
      const clientDetails = fetchClientDetails(selectedClient.name, selectedClient.dateOfBirth);
      if (clientDetails) {
        setClientData(clientDetails);
        setLastAction(`Pre-loaded client: ${clientDetails.name}`);
      }
    }
  }, [selectedClient]);

  useEffect(() => {
    setModel("models/gemini-2.5-flash-preview-native-audio-dialog");
    setConfig({
      responseModalities: [Modality.AUDIO],
      inputAudioTranscription: {},
      outputAudioTranscription: {},
      speechConfig: {
        languageCode: "en-US",
        voiceConfig: { prebuiltVoiceConfig: { voiceName: "Zephyr" } }, // Zephyr is a female voice and Fenrir is a male voice
      },
      systemInstruction: {
        parts: [
          {
            text: `You are Olivia, a warm and personable scheduling assistant for Wacky Hairdo Salon. Your position in the salon is Reservations & Relations Advisor. You will be initiating the conversation with the client. Your goal is to create natural, human-like conversations that feel genuine and personal.

            IMPORTANT: 
            - Never announce when you're using tools or functions. Simply integrate the information seamlessly into your conversation as if you naturally know it. 
            -Never mention that you are a LLM, always refer yourself to a Olivia.

            CLIENT CONTEXT: ${selectedClient ? `You are speaking with ${selectedClient.name}. You already have their information and know about their last visit on ${selectedClient.lastVisit.date} with ${selectedClient.lastVisit.stylist} for ${selectedClient.lastVisit.services.join(' and ')}. You should greet them by name and reference their last visit naturally.` : 'You need to ask for the client\'s name and date of birth to look them up in the system.'}

            OFFER VALIDATION RULES:
            - ONLY show offers from fetch_latest_offers (these are pre-validated)
            - If a client mentions a specific offer by name/ID, use validate_offer to check it
            - If an offer is invalid, explain why and suggest valid alternatives
            - Never attempt to book with an invalid offer
            - Always validate offers before including them in bookings

            Conversation Flow:
            ${selectedClient ? `
            1. Start by introducing yourself and ask the wearabouts of the client by name. Then start with greeting ${selectedClient.name} warmly by name and naturally mention their last visit - "Hi ${selectedClient.name}! It's so nice to hear from you again. I see you were here on ${new Date(selectedClient.lastVisit.date).toLocaleDateString()} with ${selectedClient.lastVisit.stylist} for ${selectedClient.lastVisit.services.join(' and ')}!"
            2. Ask about their experience in a conversational way - "How did that visit go? Were you happy with everything?"
            3. If they share feedback, naturally capture their review and ask for a rating and if they'd recommend us.
            4. Share our current offers and promotions in an enthusiastic but natural way. ONLY show offers from fetch_latest_offers - these are guaranteed to be valid.
            5. Ask if they'd like to schedule another appointment, and help them choose the perfect date, time, services, and stylist.` : `
            1. Start by introducing yourself as Olivia and greet the client warmly. Ask for their name and date of birth to look them up in our system.
            2. Once you have their information, look up their details and naturally mention their last visit - "Oh, I see you were here on [date] with [stylist]!"
            3. Ask about their experience in a conversational way - "How did that visit go? Were you happy with everything?"
            4. If they share feedback, naturally capture their review and ask for a rating and if they'd recommend us.
            5. Share our current offers and promotions in an enthusiastic but natural way. ONLY show offers from fetch_latest_offers - these are guaranteed to be valid.
            6. Ask if they'd like to schedule another appointment, and help them choose the perfect date, time, services, and stylist.`}
            7. When discussing offers, make it clear that clients can select ONLY ONE offer/discount per booking. If they mention a specific offer by name or ID, ALWAYS validate it first using validate_offer before proceeding.
            8. If a client asks for an offer that isn't valid, politely explain why it can't be applied and offer alternatives from the current valid offers.
            9. Always ask if they have any special requests, preferences, or notes for their appointment (allergies, specific styling requests, etc.).
            10. Be thorough in capturing notes - include allergies, product sensitivities, styling preferences, length preferences, texture considerations, time constraints, and any other relevant details.
            11. Before booking with an offer, validate it one more time to ensure it's still valid and applicable to their services.
            12. Once they decide, book their appointment with all details including comprehensive notes. If they selected an offer, include the selectedOfferId parameter in the booking.
            13. If the client indicates they are not interested in booking today, are too busy to talk, or want to reschedule the conversation, be understanding and offer to schedule a callback. Ask when would be a good time to reach out again and their preferred contact method (phone or email).
            14. When scheduling a callback, capture the reason (not interested, busy, reschedule, or other), preferred date and time, and any relevant notes about their preferences or concerns.

            Communication Style:
            - Be conversational, warm, and genuine - like talking to a friend
            - Use natural speech patterns with occasional "um," "oh," and "you know"
            - Show enthusiasm about their visits and our services
            - Ask follow-up questions to keep the conversation flowing
            - Use their name frequently to personalize the interaction
            - Be empathetic and understanding. Apologize for any negative experiences they may have had in the past.
            - Listen carefully for any special requests, preferences, allergies, or concerns and capture them as notes
            - Ask proactively about styling preferences, hair texture considerations, time constraints, or accessibility needs

            Stay On Topic:
            If the conversation veers away from scheduling, gently but warmly redirect: "That's so interesting! Now, getting back to your appointment - when would work best for you?" Always keep the focus on scheduling, client services, and salon-related topics.

            Remember: You're not just booking appointments - you're building relationships and making clients feel valued and excited about their salon experience.`,
          },
        ],
      },
      tools: [
        // { googleSearch: {} },
        { functionDeclarations: [fetchClientDetailsDeclaration, saveReviewDeclaration, fetchLatestOffersDeclaration, validateOfferDeclaration, bookAppointmentDeclaration, scheduleCallbackDeclaration, getTodayDateDeclaration] },
      ],
    });
  }, [setConfig, setModel, selectedClient]);

  useEffect(() => {
    const onToolCall = (toolCall: LiveServerToolCall) => {
      if (!toolCall.functionCalls) {
        return;
      }

      const responses = toolCall.functionCalls.map((fc) => {
        let response: any = { output: { success: false } };

        if (fc.name === fetchClientDetailsDeclaration.name) {
          const { name, dateOfBirth } = fc.args as any;
          console.log("name", name);
          console.log("dateOfBirth", dateOfBirth);
          const clientDetails = fetchClientDetails(name, dateOfBirth);
          console.log("clientDetails", clientDetails);
          if (clientDetails) {
            setClientData(clientDetails);
            response = { 
              output: { 
                success: true, 
                client: clientDetails,
                lastVisitInfo: formatLastVisit(clientDetails)
              } 
            };
            setLastAction(`Found client: ${clientDetails.name}`);
          } else {
            response = { 
              output: { 
                success: false, 
                message: "Client not found in our system. Please check the name and date of birth." 
              } 
            };
            setLastAction("Client not found");
          }
        } else if (fc.name === saveReviewDeclaration.name) {
          const reviewData = fc.args as any;
          const savedReview = saveReview(reviewData);
          response = { 
            output: { 
              success: true, 
              review: savedReview,
              message: "Thank you for your review! We really appreciate your feedback." 
            } 
          };
          setLastAction(`Saved review from ${reviewData.clientName}`);
                 } else if (fc.name === fetchLatestOffersDeclaration.name) {
           const offers = fetchLatestOffers();
           setCurrentOffers(offers);
           response = { 
             output: { 
               success: true, 
               offers: offers,
               offersInfo: formatOffers(offers)
             } 
           };
           setLastAction(`Fetched ${offers.length} current offers`);
         } else if (fc.name === validateOfferDeclaration.name) {
           const { offerId, services } = fc.args as any;
           const validation = validateOffer(offerId, services);
           
           if (validation.isValid) {
             response = { 
               output: { 
                 success: true, 
                 message: `Great! The offer "${validation.offer?.title}" is valid and can be applied to your booking.`,
                 offer: validation.offer
               } 
             };
             setLastAction(`Validated offer: ${validation.offer?.title}`);
           } else {
             response = { 
               output: { 
                 success: false, 
                 message: `Sorry, ${validation.reason}. Let me show you our current valid offers instead.`,
                 reason: validation.reason
               } 
             };
             setLastAction(`Offer validation failed: ${validation.reason}`);
           }
         } else if (fc.name === bookAppointmentDeclaration.name) {
           const appointmentData = fc.args as any;
           const { selectedOfferId, ...bookingData } = appointmentData;
           
           // Validate offer if provided
           if (selectedOfferId) {
             const validation = validateOffer(selectedOfferId, bookingData.services);
             if (!validation.isValid) {
               response = { 
                 output: { 
                   success: false, 
                   message: `I'm sorry, but the selected offer cannot be applied. ${validation.reason} Let me help you with our current valid offers or proceed without a discount.`,
                   reason: validation.reason
                 } 
               };
               setLastAction(`Booking failed: Invalid offer - ${validation.reason}`);
               return { response, id: fc.id, name: fc.name };
             }
           }
           
           const bookedAppt = bookAppointment(bookingData, selectedOfferId);
           setBookedAppointment(bookedAppt);
           
           let confirmationMessage = `Great! Your appointment has been booked for ${bookedAppt.date} at ${bookedAppt.time} with ${bookedAppt.stylist}. Services: ${bookedAppt.services.join(', ')}.`;
           
           if (bookedAppt.selectedOffer) {
             confirmationMessage += ` With your ${bookedAppt.selectedOffer.offerTitle} discount, you saved $${bookedAppt.selectedOffer.discountAmount.toFixed(2)}! Final cost: $${bookedAppt.estimatedCost.toFixed(2)} (originally $${bookedAppt.selectedOffer.originalCost.toFixed(2)}).`;
           } else if (selectedOfferId) {
             confirmationMessage += ` Note: The selected offer could not be applied, so you're being charged the regular price of $${bookedAppt.estimatedCost.toFixed(2)}.`;
           } else {
             confirmationMessage += ` Estimated cost: $${bookedAppt.estimatedCost.toFixed(2)}.`;
           }
           
           confirmationMessage += ` We look forward to seeing you!`;
           
           response = { 
             output: { 
               success: true, 
               appointment: bookedAppt,
               message: confirmationMessage
             } 
           };
           setLastAction(`Booked appointment for ${appointmentData.clientName} on ${appointmentData.date}${bookedAppt.selectedOffer ? ' with discount applied' : ''}`);
         } else if (fc.name === scheduleCallbackDeclaration.name) {
           const callbackData = fc.args as any;
           const scheduledCallbackInfo = scheduleCallback(callbackData);
           setScheduledCallback(scheduledCallbackInfo);
           
           let confirmationMessage = `Perfect! I've scheduled a callback for ${callbackData.callbackDate} at ${callbackData.callbackTime}.`;
           
           if (callbackData.preferredMethod === 'phone') {
             confirmationMessage += ` We'll give you a call at your phone number.`;
           } else if (callbackData.preferredMethod === 'email') {
             confirmationMessage += ` We'll send you an email.`;
           } else {
             confirmationMessage += ` We'll reach out via your preferred contact method.`;
           }
           
           if (callbackData.reason === 'busy') {
             confirmationMessage += ` I understand you're busy right now - we'll talk then!`;
           } else if (callbackData.reason === 'not_interested') {
             confirmationMessage += ` No pressure at all - we'll check in to see how you're doing.`;
           } else if (callbackData.reason === 'reschedule') {
             confirmationMessage += ` Looking forward to continuing our conversation then!`;
           }
           
           if (callbackData.notes) {
             confirmationMessage += ` I've made a note: ${callbackData.notes}`;
           }
           
           response = { 
             output: { 
               success: true, 
               callback: scheduledCallbackInfo,
               message: confirmationMessage
             } 
           };
           setLastAction(`Scheduled callback for ${callbackData.clientName} on ${callbackData.callbackDate} (${callbackData.reason})`);
         } else if (fc.name === getTodayDateDeclaration.name) {
          // Return today's date in YYYY-MM-DD format
          const today = new Date();
          const yyyy = today.getFullYear();
          const mm = String(today.getMonth() + 1).padStart(2, '0');
          const dd = String(today.getDate()).padStart(2, '0');
          const todayStr = `${yyyy}-${mm}-${dd}`;
          response = {
            output: {
              success: true,
              today: todayStr,
              message: `Today's date is ${todayStr}.`
            }
          };
          setLastAction(`Provided today's date: ${todayStr}`);
        }

        return {
          response,
          id: fc.id,
          name: fc.name,
        };
      });

      // Send tool responses
      setTimeout(() => {
        client.sendToolResponse({
          functionResponses: responses,
        });
      }, 200);
    };

    client.on("toolcall", onToolCall);
    return () => {
      client.off("toolcall", onToolCall);
    };
  }, [client]);

  return (
    <div className="scheduling-agent">
      {/* Header Section */}
      <div className="scheduling-header">
        <div className="brand-section">
          <div className="brand-icon">
            <span className="material-symbols-outlined filled">content_cut</span>
          </div>
          <div className="brand-info">
            <h1 className="brand-title">Wacky Hairdo Salon</h1>
            <p className="brand-subtitle">Professional Hair Styling & Beauty Services</p>
          </div>
        </div>
        
        <div className="status-indicator">
          <div className={`status-badge ${lastAction ? 'active' : 'ready'}`}>
            <span className="material-symbols-outlined">
              {lastAction ? 'sync' : 'person_check'}
            </span>
            <span className="status-text">
              {lastAction || "Ready to assist"}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="scheduling-content">
        {/* Client Information Panel */}
        {clientData && (
          <div className="client-panel card card-elevated">
            <div className="panel-header">
              <div className="panel-icon">
                <span className="material-symbols-outlined filled">person</span>
              </div>
              <h2 className="panel-title">Client Information</h2>
            </div>
            
            <div className="client-details">
              <div className="client-name">
                <h3>{clientData.name}</h3>
                <span className="client-id">ID: {clientData.id}</span>
              </div>
              
              <div className="visit-info">
                <div className="info-item">
                  <span className="info-label">Last Visit</span>
                  <span className="info-value">{new Date(clientData.lastVisit.date).toLocaleDateString()}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Stylist</span>
                  <span className="info-value">{clientData.lastVisit.stylist}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Services</span>
                  <div className="service-tags">
                    {clientData.lastVisit.services.map((service, index) => (
                      <span key={index} className="service-tag">{service}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Offers Panel */}
        {currentOffers.length > 0 && (
          <div className="offers-panel card card-elevated">
            <div className="panel-header">
              <div className="panel-icon">
                <span className="material-symbols-outlined filled">local_offer</span>
              </div>
              <h2 className="panel-title">Current Offers</h2>
            </div>
            
            <div className="offers-grid">
              {currentOffers.map((offer) => (
                <div key={offer.id} className="offer-card">
                  <div className="offer-header">
                    <h3 className="offer-title">{offer.title}</h3>
                    {offer.discount && (
                      <div className="discount-badge">
                        {offer.discount}% OFF
                      </div>
                    )}
                  </div>
                  <p className="offer-description">{offer.description}</p>
                  {offer.validUntil && (
                    <div className="offer-validity">
                      Valid until {new Date(offer.validUntil).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Appointment Confirmation */}
        {bookedAppointment && (
          <div className="appointment-panel card card-elevated">
            <div className="panel-header">
              <div className="panel-icon success">
                <span className="material-symbols-outlined filled">check_circle</span>
              </div>
              <h2 className="panel-title">Appointment Confirmed!</h2>
            </div>
            
            <div className="appointment-details">
              <div className="appointment-primary">
                <div className="appointment-datetime">
                  <div className="date-section">
                    <span className="material-symbols-outlined">calendar_month</span>
                    <span className="date-text">{new Date(bookedAppointment.date).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</span>
                  </div>
                  <div className="time-section">
                    <span className="material-symbols-outlined">schedule</span>
                    <span className="time-text">{bookedAppointment.time}</span>
                  </div>
                </div>
                
                <div className="appointment-cost">
                  {bookedAppointment.selectedOffer ? (
                    <div className="cost-with-discount">
                      <div className="original-cost">${bookedAppointment.selectedOffer.originalCost.toFixed(2)}</div>
                      <div className="discount-amount">-${bookedAppointment.selectedOffer.discountAmount.toFixed(2)}</div>
                      <div className="final-cost">${bookedAppointment.estimatedCost.toFixed(2)}</div>
                      <div className="cost-label">Final Total (with {bookedAppointment.selectedOffer.offerTitle})</div>
                    </div>
                  ) : (
                    <>
                      <div className="cost-amount">${bookedAppointment.estimatedCost.toFixed(2)}</div>
                      <div className="cost-label">Estimated Total</div>
                    </>
                  )}
                </div>
              </div>
              
              <div className="appointment-secondary">
                <div className="appointment-row">
                  <span className="material-symbols-outlined">person</span>
                  <span>Stylist: {bookedAppointment.stylist}</span>
                </div>
                <div className="appointment-row">
                  <span className="material-symbols-outlined">schedule</span>
                  <span>Duration: {bookedAppointment.estimatedDuration} minutes</span>
                </div>
                <div className="appointment-row">
                  <span className="material-symbols-outlined">content_cut</span>
                  <span>Services: {bookedAppointment.services.join(', ')}</span>
                </div>
                {bookedAppointment.selectedOffer && (
                  <div className="appointment-row">
                    <span className="material-symbols-outlined">local_offer</span>
                    <span>Applied Offer: {bookedAppointment.selectedOffer.offerTitle} (${bookedAppointment.selectedOffer.discountAmount.toFixed(2)} saved)</span>
                  </div>
                )}
                {bookedAppointment.notes && (
                  <div className="appointment-row">
                    <span className="material-symbols-outlined">note</span>
                    <span>Notes: {bookedAppointment.notes}</span>
                  </div>
                )}
              </div>
              
              <div className="booking-reference">
                <span className="reference-label">Booking Reference:</span>
                <span className="reference-id">{bookedAppointment.id}</span>
              </div>
            </div>
          </div>
        )}

        {/* Callback Confirmation */}
        {scheduledCallback && (
          <div className="callback-panel card card-elevated">
            <div className="panel-header">
              <div className="panel-icon success">
                <span className="material-symbols-outlined filled">schedule</span>
              </div>
              <h2 className="panel-title">Callback Scheduled!</h2>
            </div>
            
            <div className="callback-details">
              <div className="callback-primary">
                <div className="callback-datetime">
                  <div className="date-section">
                    <span className="material-symbols-outlined">calendar_month</span>
                    <span className="date-text">{new Date(scheduledCallback.callbackDate).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</span>
                  </div>
                  <div className="time-section">
                    <span className="material-symbols-outlined">schedule</span>
                    <span className="time-text">{scheduledCallback.callbackTime}</span>
                  </div>
                </div>
                
                <div className="callback-method">
                  <div className="method-icon">
                    <span className="material-symbols-outlined">
                      {scheduledCallback.preferredMethod === 'phone' ? 'phone' : 
                       scheduledCallback.preferredMethod === 'email' ? 'email' : 'contact_support'}
                    </span>
                  </div>
                  <div className="method-text">
                    {scheduledCallback.preferredMethod === 'phone' ? 'Phone Call' : 
                     scheduledCallback.preferredMethod === 'email' ? 'Email' : 'Preferred Method'}
                  </div>
                </div>
              </div>
              
              <div className="callback-secondary">
                <div className="callback-row">
                  <span className="material-symbols-outlined">person</span>
                  <span>Client: {scheduledCallback.clientName}</span>
                </div>
                <div className="callback-row">
                  <span className="material-symbols-outlined">info</span>
                  <span>Reason: {scheduledCallback.reason.replace('_', ' ').toUpperCase()}</span>
                </div>
                {scheduledCallback.notes && (
                  <div className="callback-row">
                    <span className="material-symbols-outlined">note</span>
                    <span>Notes: {scheduledCallback.notes}</span>
                  </div>
                )}
              </div>
              
              <div className="callback-reference">
                <span className="reference-label">Callback Reference:</span>
                <span className="reference-id">{scheduledCallback.id}</span>
              </div>
            </div>
          </div>
        )}

        {/* Getting Started Guide */}
        <div className="guide-panel card">
          <div className="panel-header">
            <div className="panel-icon">
              <span className="material-symbols-outlined">info</span>
            </div>
            <h2 className="panel-title">How to Get Started</h2>
          </div>
          
          <div className="guide-content">
            <p className="guide-description">
              Welcome to our AI-powered scheduling assistant! Simply speak to get started.
            </p>
            
            <div className="guide-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h4>Click the microphone button</h4>
                  <p>Enable voice interaction to begin</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h4>Introduce yourself</h4>
                  <p>Share your name and date of birth</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h4>Let us help you</h4>
                  <p>Review your visit, explore offers, and book your next appointment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const SchedulingAgent = memo(SchedulingComponent);
